import React from 'react';
import { Edit2, Trash2, Download } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

export default function DetailedExcelView({ 
  records, 
  onEdit, 
  onDelete, 
  formatCurrency,
  calculateMonthlyProfitLoss,
  calculateROI 
}) {
  // Improved safe date formatter with better error handling
  const formatSafeDate = (dateValue, formatString = 'MMM yyyy') => {
    if (!dateValue) return 'N/A';
    
    try {
      let parsedDate;
      
      // Handle different date formats
      if (typeof dateValue === 'string') {
        // Try ISO format first
        parsedDate = parseISO(dateValue);
        
        // If invalid, try creating date directly
        if (!isValid(parsedDate)) {
          parsedDate = new Date(dateValue);
        }
      } else {
        parsedDate = new Date(dateValue);
      }
      
      // Final validation check
      if (!isValid(parsedDate)) {
        console.warn('Invalid date value:', dateValue);
        return 'N/A';
      }
      
      return format(parsedDate, formatString);
    } catch (error) {
      console.error('Date formatting error:', error, 'Value:', dateValue);
      return 'N/A';
    }
  };

  const calculateROAS = (record) => {
    // Use database ROAS if available, otherwise calculate
    if (record?.roas && record?.roas > 0) {
      return record?.roas;
    }
    
    const ppcSpend = parseFloat(record?.googleAdsExpense || 0);
    const totalRevenue = parseFloat(record?.existingClientRevenue || 0) + parseFloat(record?.newClientRevenue || 0);
    
    if (ppcSpend === 0) return 0;
    return totalRevenue / ppcSpend;
  };

  const exportToCSV = () => {
    const headers = [
      'Month',
      'Existing Client Revenue',
      'New Client Revenue', 
      'Total Revenue',
      'Google Ads Expense',
      'ROAS',
      'Other Costs',
      'Total Expenses',
      'Gross Profit',
      'Net Profit',
      'Profit Margin %',
      'ROI %'
    ];

    const csvData = records?.map(record => {
      const profitData = calculateMonthlyProfitLoss(record);
      const roi = calculateROI(record);
      const roas = calculateROAS(record);

      return [
        new Date(record?.trackingMonth)?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        record?.existingClientRevenue || 0,
        record?.newClientRevenue || 0,
        profitData?.totalRevenue,
        record?.googleAdsExpense || 0,
        roas?.toFixed(2),
        record?.otherMonthlyCosts || 0,
        profitData?.totalExpenses,
        profitData?.grossProfit,
        profitData?.netProfit,
        profitData?.profitMarginPercentage?.toFixed(2),
        roi?.toFixed(2)
      ];
    });

    const csv = [headers, ...csvData]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-performance-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  if (!records || records?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <p className="text-gray-500">No monthly data available. Add your first entry to get started.</p>
        </div>
      </div>
    );
  }

  // Calculate totals for summary
  const totalExistingRevenue = records?.reduce((sum, r) => sum + (r?.existingClientRevenue || 0), 0);
  const totalNewRevenue = records?.reduce((sum, r) => sum + (r?.newClientRevenue || 0), 0);
  const totalExistingClients = records?.reduce((sum, r) => sum + (r?.existingClientSales || 0), 0);
  const totalNewClients = records?.reduce((sum, r) => sum + (r?.newClientSales || 0), 0);
  const totalOtherCosts = records?.reduce((sum, r) => sum + (r?.otherMonthlyCosts || 0), 0);
  const totalGoogleAds = records?.reduce((sum, r) => sum + (r?.googleAdsExpense || 0), 0);
  
  const totalRevenue = records?.reduce((sum, r) => {
    const pl = calculateMonthlyProfitLoss(r);
    return sum + (pl?.totalRevenue || 0);
  }, 0);
  
  const totalNetProfit = records?.reduce((sum, r) => {
    const pl = calculateMonthlyProfitLoss(r);
    return sum + (pl?.netProfit || 0);
  }, 0);
  
  const avgROI = records?.reduce((sum, r) => {
    return sum + (calculateROI(r) || 0);
  }, 0) / records?.length;

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Detailed Excel View</h2>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
        >
          <Download size={16} />
          Export to CSV
        </button>
      </div>
      {/* Simplified Excel-Style Table - NO Horizontal Scrolling */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-600 to-indigo-600">
                <th className="sticky left-0 z-20 px-4 py-3 text-left text-xs font-bold text-white uppercase bg-gradient-to-r from-blue-600 to-indigo-600">Month</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase">Existing Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase">New Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase bg-blue-700">Total Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase">PPC Spend</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase bg-cyan-600">ROAS</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase">Other Costs</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase bg-red-700">Total Expenses</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase">Gross Profit</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase bg-green-700">Net Profit</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase">Margin %</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase">ROI %</th>
                <th className="sticky right-0 z-20 px-4 py-3 text-center text-xs font-bold text-white uppercase bg-gradient-to-r from-blue-600 to-indigo-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records?.map((record, idx) => {
                const profitData = calculateMonthlyProfitLoss(record);
                const roi = calculateROI(record);
                const roas = calculateROAS(record);

                return (
                  <tr key={record?.id} className={`border-b border-gray-200 hover:bg-blue-50 transition-colors ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <td className="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 bg-white border-r-2 border-gray-200">
                      {new Date(record?.trackingMonth)?.toLocaleDateString('en-US', { 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                      {formatCurrency(record?.existingClientRevenue || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                      {formatCurrency(record?.newClientRevenue || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-blue-900 bg-blue-50">
                      {formatCurrency(profitData?.totalRevenue)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                      {formatCurrency(record?.googleAdsExpense || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold bg-cyan-50">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${
                        roas >= 4 ? 'text-green-800' :
                        roas >= 2 ? 'text-yellow-800': 'text-red-800'
                      }`}>
                        {roas?.toFixed(2)}x
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                      {formatCurrency(record?.otherMonthlyCosts || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-red-900 bg-red-50">
                      {formatCurrency(profitData?.totalExpenses)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                      {formatCurrency(profitData?.grossProfit)}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-bold ${
                      profitData?.isProfit ? 'text-green-900 bg-green-50' : 'text-red-900 bg-red-50'
                    }`}>
                      {formatCurrency(profitData?.netProfit)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${
                        profitData?.profitMarginPercentage >= 20 ? 'bg-green-100 text-green-800' :
                        profitData?.profitMarginPercentage >= 10 ? 'bg-yellow-100 text-yellow-800': 'bg-red-100 text-red-800'
                      }`}>
                        {profitData?.profitMarginPercentage?.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${
                        roi >= 100 ? 'bg-green-100 text-green-800' :
                        roi >= 0 ? 'bg-yellow-100 text-yellow-800': 'bg-red-100 text-red-800'
                      }`}>
                        {roi?.toFixed(1)}%
                      </span>
                    </td>
                    <td className="sticky right-0 z-10 px-4 py-3 whitespace-nowrap text-sm bg-white border-l-2 border-gray-200">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => onEdit(record)}
                          className="text-blue-600 hover:text-blue-900 hover:bg-blue-100 p-2 rounded-lg transition-all"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => onDelete(record?.id)}
                          className="text-red-600 hover:text-red-900 hover:bg-red-100 p-2 rounded-lg transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Summary Totals Row */}
            <tfoot>
              <tr className="bg-gray-100 border-t-2 border-gray-400 font-bold">
                <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300 uppercase">
                  Totals
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-300">
                  {formatCurrency(totalExistingRevenue)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-300">
                  {totalExistingClients}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-300">
                  {formatCurrency(totalNewRevenue)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-300">
                  -
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-300">
                  -
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-300">
                  {formatCurrency(totalOtherCosts)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-300">
                  {formatCurrency(totalGoogleAds)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-blue-700 border-r border-gray-300">
                  {formatCurrency(totalRevenue)}
                </td>
                <td className={`px-4 py-3 text-sm text-right border-r border-gray-300 ${
                  totalNetProfit >= 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  {formatCurrency(totalNetProfit)}
                </td>
                <td className={`px-4 py-3 text-sm text-right border-r border-gray-300 ${
                  avgROI >= 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  {avgROI?.toFixed(2)}%
                </td>
                <td className="px-4 py-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}